import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/* The API reads and writes shop-local dates with no offset, so every date the
   UI sends or parses has to be built in the shop's zone. Using the browser's
   own zone would shift the range by a day for anyone travelling. */
export const TZ = "America/Santo_Domingo";

export const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

export const formatMoney = (amount, currency) => {
	const code = currency || "DOP";
	const value = Number(amount ?? 0).toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	});
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${value}` : `${code} ${value}`;
};

/** Compact form for chart axes, where RD$12,500 is too wide to sit under a bar. */
export const formatMoneyShort = (amount, currency) => {
	const n = Number(amount ?? 0);
	const symbol = CURRENCY_SYMBOLS[currency || "DOP"] || "";
	if (Math.abs(n) >= 1000000) return `${symbol}${(n / 1000000).toFixed(1)}M`;
	if (Math.abs(n) >= 1000) return `${symbol}${(n / 1000).toFixed(1)}k`;
	return `${symbol}${n.toFixed(0)}`;
};

export const today = () => dayjs().tz(TZ);

export const toApiDate = (d) => d.format("YYYY-MM-DD");

/* ── Parsing shop-local strings ──

   ⚠️ dayjs.tz THROWS on a string it cannot parse — it does not return an
   invalid date — so the usual `dayjs.tz(x).isValid()` guard never runs. Every
   parse in this file goes through here instead.

   This is not hypothetical: formatBucket built "2026-09-05-01" out of a day
   bucket while the picker said "month", dayjs.tz threw out of the middle of
   RevenueChart's render, and React unmounted the whole reports page. A caller
   passing something unexpected should cost one label, not the screen. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_ONLY = /^\d{4}-\d{2}$/;

const parseLocal = (value) => {
	const raw = String(value ?? "").trim();
	if (!raw) return null;

	try {
		const d = dayjs.tz(raw, TZ);
		return d.isValid() ? d : null;
	} catch {
		return null;
	}
};

/** "2026-03-04T10:00:00" -> "4 Mar 2026, 10:00 AM" in the shop's zone. */
export const formatDateTime = (value, language) => {
	const d = parseLocal(value);
	if (!d) return "—";
	return d.format(language === "es" ? "D MMM YYYY, h:mm A" : "MMM D, YYYY h:mm A");
};

export const formatDate = (value, language) => {
	const d = parseLocal(String(value ?? "").slice(0, 10));
	if (!d) return "—";
	return d.format(language === "es" ? "D MMM YYYY" : "MMM D, YYYY");
};

/* Series buckets are a date for day/week and "YYYY-MM" for month.

   The bucket's own SHAPE decides how it is read, not the `granularity`
   argument. Those two disagree for one render every time Group by changes: the
   picker updates immediately while the series is still the previously fetched
   one, so a "month" label was being asked of a day bucket. Trusting the data
   over the prop makes that frame render the old grouping correctly instead of
   throwing, and the correct labels arrive with the next response. */
export const formatBucket = (period, granularity, language) => {
	const raw = String(period ?? "").trim();
	if (!raw) return "";

	if (MONTH_ONLY.test(raw)) {
		const d = parseLocal(`${raw}-01`);
		return d ? d.format("MMM YY") : raw;
	}

	if (!DATE_ONLY.test(raw)) return raw;

	/* A week bucket is stored as its Monday, and used to be formatted "8 Jun" —
	   character for character what a DAY bucket looks like. Switching Group by
	   from Day to Week therefore changed the number of bars but left the axis
	   reading the same way, which is what made the control look broken. Showing
	   the span the bar actually covers is the difference. */
	if (granularity === "week") {
		const start = parseLocal(raw);
		if (!start) return raw;
		const end = start.add(6, "day");
		return start.month() === end.month()
			? `${start.format("D")}–${end.format("D MMM")}`
			: `${start.format("D MMM")}–${end.format("D MMM")}`;
	}

	const d = parseLocal(raw);
	return d ? d.format("D MMM") : raw;
};

/* The dates a preset actually resolved to, as one compact label.

   "Last 30 days" and "This month" name a rule, not a period, and the report
   had nothing on screen saying which days it had settled on — so two people
   reading the same figure could not be sure they were reading the same month.
   Shared units are said once: "1–30 Sep 2026", not "1 Sep 2026 – 30 Sep 2026". */
export const formatRangeLabel = (from, to, language) => {
	const a = parseLocal(from);
	const b = parseLocal(to);
	if (!a || !b) return "";

	const dayMonth = language === "es" ? "D MMM" : "MMM D";

	if (a.isSame(b, "day")) return a.format(`${dayMonth}, YYYY`);
	if (a.isSame(b, "month")) {
		// Same month: "1–30 Sep 2026" / "Sep 1–30, 2026"
		return language === "es"
			? `${a.format("D")}–${b.format("D MMM YYYY")}`
			: `${a.format("MMM D")}–${b.format("D, YYYY")}`;
	}
	if (a.isSame(b, "year")) return `${a.format(dayMonth)} – ${b.format(`${dayMonth}, YYYY`)}`;

	return `${a.format(`${dayMonth}, YYYY`)} – ${b.format(`${dayMonth}, YYYY`)}`;
};

/** Date bounds for each preset, in the shop's zone. */
export const rangeForPreset = (preset) => {
	const now = today();

	switch (preset) {
		case "last7":
			return { from: toApiDate(now.subtract(6, "day")), to: toApiDate(now) };
		case "last90":
			return { from: toApiDate(now.subtract(89, "day")), to: toApiDate(now) };
		case "thisMonth":
			return { from: toApiDate(now.startOf("month")), to: toApiDate(now) };
		case "lastMonth": {
			const prev = now.subtract(1, "month");
			return { from: toApiDate(prev.startOf("month")), to: toApiDate(prev.endOf("month")) };
		}
		case "thisYear":
			return { from: toApiDate(now.startOf("year")), to: toApiDate(now) };
		case "last30":
		default:
			return { from: toApiDate(now.subtract(29, "day")), to: toApiDate(now) };
	}
};

/* ── Phone numbers ──
   Bookings store whatever the customer typed: "8095551234", "+18095551234",
   "1 809 555 1234". Rendered raw in the client card they were an unbroken run of
   digits nobody could read back over a phone, so they are grouped here.

   The Dominican Republic and the rest of the NANP share the +1 country code and
   a fixed 10-digit national number, which is the only shape that can be grouped
   with certainty. Anything else is spaced by its own country code and left
   alone rather than forced into a pattern it does not have — a wrongly grouped
   number is worse than an ungrouped one. */
const NANP = /^1?(\d{3})(\d{3})(\d{4})$/;

export const formatPhone = (value) => {
	const raw = String(value || "").trim();
	if (!raw) return "";

	const digits = raw.replace(/\D/g, "");
	const nanp = digits.match(NANP);
	if (nanp) return `+1 (${nanp[1]}) ${nanp[2]}-${nanp[3]}`;

	// Not a shape we can group. Keep every digit; just mark it as international.
	if (!digits) return raw;
	return raw.startsWith("+") ? raw : `+${digits}`;
};

/* The tel: target — no spacing, and a country code that is actually a country
   code. Naively prefixing "+" to what the booking stored turned the local
   "8095551234" into "+809 5551234", where +809 is not a dialling code at all
   (809 is a Dominican AREA code); a phone handed that either fails or dials
   something else. The same NANP test that formats the number supplies the 1. */
export const telHref = (value) => {
	const raw = String(value || "").trim();
	if (!raw) return null;

	// An explicit + is the caller's own claim about the country code; trust it.
	const digits = raw.replace(/\D/g, "");
	if (raw.startsWith("+")) return digits ? `+${digits}` : null;
	if (!digits) return null;

	return NANP.test(digits) ? `+1${digits.slice(-10)}` : `+${digits}`;
};

/* Avatar monogram. Falls back to the first character of an email local part when
   no name was ever collected, which is the common case for guest bookings. */
export const initials = (value) => {
	const raw = (value || "").trim();
	if (!raw) return "?";

	const source = raw.includes("@") ? raw.split("@")[0].replace(/[._-]+/g, " ") : raw;
	const parts = source.split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Fills {{token}} placeholders in a translation string. */
export const fill = (template, values) =>
	Object.entries(values || {}).reduce(
		(out, [key, value]) => out.replace(new RegExp(`{{${key}}}`, "g"), String(value)),
		template || "",
	);

/* Minutes of warning a cancellation gave, in words. Negative means the booking
   was cancelled after its slot had already started — routine when staff tidy up
   yesterday's no-shows, and misleading if rendered as "0 minutes of notice". */
export const formatNotice = (minutes, t) => {
	if (minutes === null || minutes === undefined) return "—";
	if (minutes < 0) return t.afterStart;
	if (minutes < 60) return fill(t.minutesNotice, { n: minutes });
	if (minutes < 1440) return fill(t.hoursNotice, { n: Math.round(minutes / 60) });
	return fill(t.daysNotice, { n: Math.round(minutes / 1440) });
};

/* A span of minutes as a compact duration: "45 min", "1h 30m", "84h".
   preferDays switches to whole days past the 24h mark, which suits lead times
   ("18 days" beats "432h") but not totals, where hours are what an owner counts
   their week in. */
export const formatDuration = (minutes, t, { preferDays = false } = {}) => {
	if (minutes === null || minutes === undefined) return "—";

	const n = Math.max(Math.round(minutes), 0);
	if (preferDays && n >= 1440) return fill(t.daysShort, { n: Math.round(n / 1440) });
	if (n < 60) return fill(t.minutesShort, { n });

	const h = Math.floor(n / 60);
	const m = n % 60;
	return m === 0 ? fill(t.hoursShort, { n: h }) : fill(t.hoursMinutes, { h, m });
};

/** An hour of the day as a clock label, e.g. 14 -> "2 PM". */
export const formatHour = (hour) => dayjs().tz(TZ).hour(hour).minute(0).format("h A");

/* ── Which groupings are worth offering for a given range ──
   Grouping a week of trade by month draws one lone bar, and grouping a year by
   day draws 365 slivers. Both technically "work", which is exactly why the
   control felt like it did nothing. Only the readable options are shown, and
   the caller drops the picker entirely when just one survives. */
export const rangeLengthDays = (from, to) => {
	const a = parseLocal(from);
	const b = parseLocal(to);
	if (!a || !b) return 0;
	return b.diff(a, "day") + 1;
};

export const allowedGranularities = (from, to) => {
	const days = rangeLengthDays(from, to);
	const out = [];
	if (days > 0 && days <= 120) out.push("day");
	if (days >= 14) out.push("week");
	if (days >= 60) out.push("month");
	// A range too odd for any rule still needs something to draw.
	return out.length ? out : ["day"];
};

/** The grouping to fall back to when the current one stops making sense. */
export const defaultGranularity = (from, to) => {
	const allowed = allowedGranularities(from, to);
	const days = rangeLengthDays(from, to);
	if (days > 120 && allowed.includes("month")) return "month";
	if (days > 45 && allowed.includes("week")) return "week";
	return allowed[0];
};
